import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiDollarSign, FiTrendingUp, FiUsers, FiPackage, FiLayers,
  FiCheckCircle, FiAlertCircle, FiClock, FiX, FiRefreshCw,
  FiActivity, FiZap, FiArrowRight,
} from 'react-icons/fi';
import { getAdminMetrics, getAdminStats, AdminMetrics, AdminStats } from '../../api/admin';

/* ── helpers ───────────────────────────────────────────────────────────────── */
const fmt = (amount: number, currency = 'EUR') =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency }).format(amount);

const fmtTime = (d: Date) =>
  d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

const PLAN_COLOR: Record<string, string> = {
  free:       'text-gray-500',
  standard:   'text-blue-600',
  entreprise: 'text-purple-600',
  business:   'text-indigo-600',
};
const PLAN_BG: Record<string, string> = {
  free:       'bg-gray-100',
  standard:   'bg-blue-100',
  entreprise: 'bg-purple-100',
  business:   'bg-indigo-100',
};
const PLAN_BAR: Record<string, string> = {
  free:       'bg-gray-400',
  standard:   'bg-blue-500',
  entreprise: 'bg-purple-500',
  business:   'bg-indigo-500',
};
const planLabel = (p: string) => p.charAt(0).toUpperCase() + p.slice(1);

/* ── sub-components ─────────────────────────────────────────────────────────── */
const StatCard: React.FC<{
  label: string; value: React.ReactNode; sub?: string;
  color: string; icon: React.ReactNode; onClick?: () => void;
}> = ({ label, value, sub, color, icon, onClick }) => (
  <div
    onClick={onClick}
    className={`bg-white rounded-xl border border-gray-200 p-5 shadow-sm flex items-start gap-4
      ${onClick ? 'cursor-pointer hover:shadow-md hover:border-gray-300 transition-all group' : ''}`}
  >
    <div className={`p-2.5 rounded-lg ${color} flex-shrink-0`}>{icon}</div>
    <div className="min-w-0">
      <div className="text-2xl font-bold text-gray-900 leading-tight">{value}</div>
      <div className="text-sm text-gray-500 mt-0.5">{label}</div>
      {sub && <div className="text-xs text-gray-400 mt-1">{sub}</div>}
    </div>
    {onClick && <FiArrowRight className="ml-auto text-gray-300 group-hover:text-gray-500 transition-colors flex-shrink-0 mt-1" size={16} />}
  </div>
);

const ProgressBar: React.FC<{ pct: number; color: string }> = ({ pct, color }) => (
  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
    <div className={`h-full rounded-full transition-all duration-500 ${color}`} style={{ width: `${Math.min(pct, 100)}%` }} />
  </div>
);

/* ── main component ─────────────────────────────────────────────────────────── */
const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [metrics, setMetrics]   = useState<AdminMetrics | null>(null);
  const [stats, setStats]       = useState<AdminStats | null>(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const [m, s] = await Promise.all([getAdminMetrics(), getAdminStats()]);
      setMetrics(m.metrics);
      setStats(s);
      setLastUpdate(new Date());
    } catch (err: any) {
      const msg = err.response?.data?.details || err.response?.data?.error || 'Erreur de chargement';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-gray-500 text-sm">Chargement des métriques...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="p-6">
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
        <FiAlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
        <div className="flex-1">
          <h3 className="font-semibold text-red-800">Erreur</h3>
          <p className="text-red-600 text-sm mt-1">{error}</p>
        </div>
        <button onClick={load} className="px-3 py-1 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg text-sm font-medium transition-colors">
          Réessayer
        </button>
      </div>
    </div>
  );

  if (!metrics) return null;

  const totalMrr = metrics.revenue.mrr || 1;
  const conversionRate = metrics.health.trial_conversion_rate;
  const totalSubs = (metrics.subscriptions.active_trials || 0) + (metrics.subscriptions.converted || 0) +
                    (metrics.subscriptions.expired_trials || 0) + (metrics.subscriptions.canceled || 0);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-8">

      {/* ── Header ── */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard Admin</h1>
          <p className="text-gray-500 text-sm mt-0.5">Vue d'ensemble des métriques et revenus</p>
        </div>
        <div className="flex items-center gap-3">
          {lastUpdate && (
            <span className="text-xs text-gray-400 flex items-center gap-1">
              <FiClock size={12} />
              Mis à jour à {fmtTime(lastUpdate)}
            </span>
          )}
          <button
            onClick={load} disabled={loading}
            className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg text-sm transition-colors disabled:opacity-50"
          >
            <FiRefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Actualiser
          </button>
        </div>
      </div>

      {/* ── KPIs principaux ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-5 text-white shadow-md">
          <div className="flex items-center justify-between mb-1">
            <span className="text-green-100 text-xs font-medium uppercase tracking-wide">MRR</span>
            <FiTrendingUp className="text-green-200" size={16} />
          </div>
          <div className="text-2xl font-bold">{fmt(metrics.revenue.mrr, metrics.revenue.currency)}</div>
          <div className="text-green-100 text-xs mt-1">Revenu mensuel récurrent</div>
        </div>
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white shadow-md">
          <div className="flex items-center justify-between mb-1">
            <span className="text-blue-100 text-xs font-medium uppercase tracking-wide">ARR</span>
            <FiTrendingUp className="text-blue-200" size={16} />
          </div>
          <div className="text-2xl font-bold">{fmt(metrics.revenue.arr, metrics.revenue.currency)}</div>
          <div className="text-blue-100 text-xs mt-1">Revenu annuel récurrent</div>
        </div>
        <div
          className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-5 text-white shadow-md cursor-pointer hover:opacity-90 transition-opacity"
          onClick={() => navigate('/admin/organizations')}
        >
          <div className="flex items-center justify-between mb-1">
            <span className="text-purple-100 text-xs font-medium uppercase tracking-wide">Organisations</span>
            <FiPackage className="text-purple-200" size={16} />
          </div>
          <div className="text-2xl font-bold">{metrics.organizations.total}</div>
          <div className="text-purple-100 text-xs mt-1">+{metrics.organizations.new_last_30_days} ce mois</div>
        </div>
        <div
          className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl p-5 text-white shadow-md cursor-pointer hover:opacity-90 transition-opacity"
          onClick={() => navigate('/admin/users')}
        >
          <div className="flex items-center justify-between mb-1">
            <span className="text-indigo-100 text-xs font-medium uppercase tracking-wide">Utilisateurs</span>
            <FiUsers className="text-indigo-200" size={16} />
          </div>
          <div className="text-2xl font-bold">{metrics.users.total}</div>
          <div className="text-indigo-100 text-xs mt-1">Utilisateurs actifs</div>
        </div>
      </div>

      {/* ── Revenus par plan + Abonnements ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Revenus par plan */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FiDollarSign className="text-green-600" size={18} />
            Revenus par plan
          </h2>
          <div className="space-y-4">
            {Object.entries(metrics.revenue.by_plan).map(([plan, rev]) => {
              const pct = Math.round((rev.total_mrr / totalMrr) * 100);
              return (
                <div key={plan}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className={`text-sm font-semibold ${PLAN_COLOR[plan] || 'text-gray-600'}`}>
                      {planLabel(plan)}
                    </span>
                    <div className="text-right">
                      <span className="text-sm font-bold text-gray-900">{fmt(rev.total_mrr, metrics.revenue.currency)}</span>
                      <span className="text-xs text-gray-400 ml-1.5">{pct}%</span>
                    </div>
                  </div>
                  <ProgressBar pct={pct} color={PLAN_BAR[plan] || 'bg-gray-400'} />
                  <div className="text-xs text-gray-400 mt-1">
                    Mensuel: {fmt(rev.monthly, metrics.revenue.currency)} · Annuel: {fmt(rev.yearly, metrics.revenue.currency)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Abonnements */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2 justify-between">
            <span className="flex items-center gap-2">
              <FiPackage className="text-purple-600" size={18} />
              Abonnements
            </span>
            <button onClick={() => navigate('/admin/subscriptions')} className="text-xs text-indigo-500 hover:text-indigo-700 flex items-center gap-1">
              Voir tout <FiArrowRight size={12} />
            </button>
          </h2>
          <div className="grid grid-cols-2 gap-3 mb-4">
            {[
              { label: 'Essais actifs',   val: metrics.subscriptions.active_trials,  bg: 'bg-yellow-50', color: 'text-yellow-600', icon: <FiClock size={16} /> },
              { label: 'Convertis',       val: metrics.subscriptions.converted,      bg: 'bg-green-50',  color: 'text-green-600',  icon: <FiCheckCircle size={16} /> },
              { label: 'Essais expirés',  val: metrics.subscriptions.expired_trials, bg: 'bg-orange-50', color: 'text-orange-600', icon: <FiAlertCircle size={16} /> },
              { label: 'Annulés',         val: metrics.subscriptions.canceled,       bg: 'bg-red-50',    color: 'text-red-500',    icon: <FiX size={16} /> },
            ].map(({ label, val, bg, color, icon }) => (
              <div key={label} className={`${bg} rounded-lg p-3 flex items-center gap-2`}>
                <span className={color}>{icon}</span>
                <div>
                  <div className={`text-lg font-bold ${color}`}>{val}</div>
                  <div className="text-xs text-gray-500">{label}</div>
                </div>
              </div>
            ))}
          </div>
          {/* Distribution par plan */}
          <div className="border-t border-gray-100 pt-3">
            <p className="text-xs text-gray-500 font-medium mb-2 uppercase tracking-wide">Distribution par plan</p>
            <div className="space-y-1.5">
              {Object.entries(metrics.subscriptions.by_plan).map(([plan, c]) => {
                const pct = totalSubs > 0 ? Math.round((c.total / totalSubs) * 100) : 0;
                return (
                  <div key={plan} className="flex items-center gap-2">
                    <span className={`text-xs font-medium w-20 flex-shrink-0 ${PLAN_COLOR[plan] || 'text-gray-500'}`}>
                      {planLabel(plan)}
                    </span>
                    <div className="flex-1">
                      <ProgressBar pct={pct} color={PLAN_BAR[plan] || 'bg-gray-400'} />
                    </div>
                    <span className="text-xs text-gray-500 w-6 text-right">{c.total}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* ── Santé + Exécutions ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Taux de conversion */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FiTrendingUp className="text-blue-600" size={18} />
            Conversion essais
          </h2>
          <div className="flex items-end gap-2 mb-3">
            <span className="text-4xl font-bold text-gray-900">{conversionRate}</span>
            <span className="text-lg text-gray-400 mb-1">%</span>
          </div>
          <ProgressBar pct={conversionRate} color="bg-blue-500" />
          <div className="flex justify-between text-xs text-gray-400 mt-1.5">
            <span>0%</span><span>50%</span><span>100%</span>
          </div>
          <p className="text-xs text-gray-500 mt-3">
            {metrics.subscriptions.converted} convertis sur{' '}
            {(metrics.subscriptions.converted || 0) + (metrics.subscriptions.expired_trials || 0)} essais terminés
          </p>
        </div>

        {/* Paiements échoués */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FiActivity className="text-red-500" size={18} />
            Santé paiements
          </h2>
          <div className={`flex items-center gap-3 p-3 rounded-lg ${metrics.health.payment_failures > 0 ? 'bg-red-50' : 'bg-green-50'}`}>
            <div className={`p-2 rounded-full ${metrics.health.payment_failures > 0 ? 'bg-red-100' : 'bg-green-100'}`}>
              {metrics.health.payment_failures > 0
                ? <FiAlertCircle className="text-red-600" size={20} />
                : <FiCheckCircle className="text-green-600" size={20} />
              }
            </div>
            <div>
              <div className={`text-2xl font-bold ${metrics.health.payment_failures > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {metrics.health.payment_failures}
              </div>
              <div className="text-sm text-gray-600">Paiements échoués</div>
            </div>
          </div>
          {metrics.health.payment_failures === 0 && (
            <p className="text-xs text-green-600 mt-3 flex items-center gap-1">
              <FiCheckCircle size={12} /> Tous les paiements sont à jour
            </p>
          )}
        </div>

        {/* Exécutions Terraform */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FiZap className="text-orange-500" size={18} />
            Exécutions Terraform
          </h2>
          {stats ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                <span className="text-sm text-gray-600">7 derniers jours</span>
                <span className="text-2xl font-bold text-orange-600">{stats.executions.last_7d}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-600">Total historique</span>
                <span className="text-xl font-bold text-gray-700">{stats.executions.total}</span>
              </div>
            </div>
          ) : (
            <div className="text-gray-400 text-sm">Données non disponibles</div>
          )}
        </div>
      </div>

      {/* ── Ressources ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {[
          { title: 'Projets', data: metrics.resources.projects },
          { title: 'Architectures', data: metrics.resources.architectures },
        ].map(({ title, data }) => {
          const max = Math.max(...Object.values(data.by_plan).map(Number), 1);
          return (
            <div key={title} className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <h2 className="font-semibold text-gray-900 mb-1 flex items-center gap-2">
                <FiLayers className="text-teal-600" size={18} />
                {title}
              </h2>
              <div className="text-3xl font-bold text-gray-900 mb-4">{data.total}</div>
              <div className="space-y-3">
                {Object.entries(data.by_plan).map(([plan, count]) => {
                  const pct = Math.round(((count as number) / max) * 100);
                  return (
                    <div key={plan}>
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-sm font-medium ${PLAN_COLOR[plan] || 'text-gray-500'}`}>{planLabel(plan)}</span>
                        <span className="text-sm font-bold text-gray-900">{count as number}</span>
                      </div>
                      <ProgressBar pct={pct} color={PLAN_BAR[plan] || 'bg-gray-400'} />
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Activité récente ── */}
      {metrics.recent_activity && metrics.recent_activity.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FiActivity className="text-indigo-600" size={18} />
            Activité récente — Nouveaux abonnements
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 px-3 font-semibold text-gray-600">Date</th>
                  <th className="text-left py-2 px-3 font-semibold text-gray-600">Plan</th>
                  <th className="text-center py-2 px-3 font-semibold text-gray-600">Nouveaux</th>
                </tr>
              </thead>
              <tbody>
                {metrics.recent_activity.slice(0, 10).map((row, i) => (
                  <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="py-2 px-3 text-gray-600">
                      {new Date(row.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </td>
                    <td className="py-2 px-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${PLAN_BG[row.plan] || 'bg-gray-100'} ${PLAN_COLOR[row.plan] || 'text-gray-600'}`}>
                        {planLabel(row.plan)}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-center font-bold text-gray-900">{row.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Actions rapides ── */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <FiZap className="text-yellow-500" size={18} />
          Actions rapides
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Gérer les organisations', path: '/admin/organizations', icon: <FiPackage size={18} />, color: 'text-blue-600 bg-blue-50 hover:bg-blue-100' },
            { label: 'Gérer les utilisateurs',  path: '/admin/users',         icon: <FiUsers size={18} />,   color: 'text-indigo-600 bg-indigo-50 hover:bg-indigo-100' },
            { label: 'Voir les abonnements',    path: '/admin/subscriptions', icon: <FiDollarSign size={18} />, color: 'text-green-600 bg-green-50 hover:bg-green-100' },
            { label: 'Infra ADO',               path: '/admin/infrastructure', icon: <FiActivity size={18} />, color: 'text-orange-600 bg-orange-50 hover:bg-orange-100' },
          ].map(({ label, path, icon, color }) => (
            <button
              key={path}
              onClick={() => navigate(path)}
              className={`flex items-center gap-2.5 px-4 py-3 rounded-lg text-sm font-medium transition-colors text-left ${color}`}
            >
              {icon}
              <span>{label}</span>
            </button>
          ))}
        </div>
      </div>

    </div>
  );
};

export default AdminDashboard;
